import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DragDropContext, Draggable, Droppable, type DropResult } from "@hello-pangea/dnd";
import { toast } from "sonner";
import { ArrowDown, ArrowRight, ArrowUp, ChevronLeft, ChevronRight, Columns3, FileText, List, Lock, Phone, Rows3, Search, StickyNote, Undo2, X } from "lucide-react";
import {
  columnaDe,
  etapaDeColumna,
  ETAPAS,
  ETIQUETA_COLUMNA,
  ETIQUETA_VEREDICTO,
  fechaAmigable,
  grupoDe,
  linkLlamar,
  linkWhatsApp,
} from "@/reclutamiento/logica";
import { contarPorPlaza, esClavePlaza, plazaDe, plazaPorClave, TODAS_LAS_PLAZAS, type ClavePlaza } from "@/reclutamiento/plazas";
import type { CandidatoPortal, Columna, Etapa, Veredicto } from "@/reclutamiento/tipos";
import { BannerPago, CajaError, Esqueleto, HojaDescartar, HojaMover } from "../componentes";
import { Faltantes, Leyenda, Requisitos } from "../claridad";
import { SUBTITULO_COLUMNA, textosDeVacantes, TITULO_VEREDICTO, type TextosRequisitos } from "../claridad-textos";
import { usePortal } from "../contexto";
import { ChipEtapa, IcWhatsApp, IconoEtapa, Mk, Nuevo } from "../iconos";
import { claseEtapa, clasePlaza, ETAPA_DE_VEREDICTO, IconoPlaza, PESTANA_VISUAL } from "../etapas";
import { descartarCandidato, moverCandidato, regresarCandidato } from "../mover";
import { leerRuta, navegar, ruta, useRuta } from "../ruta";
import {
  clavePestana as clavePestanaDe,
  clavePlaza as clavePlazaDe,
  coincide,
  COLUMNAS_DE,
  enPestana,
  esEtapaFiltro,
  esNuevoDesde,
  esPestana,
  ETIQUETA_ETAPA_FILTRO,
  esVista,
  etiquetaVista,
  filasLista,
  guardarEstadoLista,
  guardarSecuencia,
  leerEstadoLista,
  ordenDeVista,
  ordenTablero,
  ordenTarjetas,
  PESTANAS,
  pestanaRecordada,
  type EstadoLista,
  type EtapaFiltro,
  type Orden,
  type Pestana,
  type Vista,
} from "../secuencia";
import { claveCache, ETAPA_CLIENTE, guardarPref, leerPref, mensajeError, semDeVeredicto, useAngosto, useCarga } from "../util";

const PREF = { leer: leerPref, guardar: guardarPref };

const VISTAS: [Vista, string, typeof List][] = [
  ["tablero", "Tablero", Columns3],
  ["lista", "Lista", List],
  ["tarjetas", "Tarjetas", Rows3],
];

/** «Nota»: que se vea desde la lista que ya le escribiste algo (el texto, al pasar el mouse o en la ficha). */
function ConNota({ k }: { k: CandidatoPortal }) {
  if (!k.nota_cliente) return null;
  return (
    <span className="connota" title={`Tu nota: ${k.nota_cliente}`} aria-label={`Tiene nota: ${k.nota_cliente}`}>
      <StickyNote size={12} aria-hidden="true" /> Nota
    </span>
  );
}

/** La línea de «por qué» de una tarjeta: si lo descartó el cliente, su motivo; si no, el del filtro. */
function Porque({ k, className = "why" }: { k: CandidatoPortal; className?: string }) {
  if (k.etapa === "descartado")
    return <p className={className + " motcli"}>{k.motivo_cliente ? `Lo descartaste: ${k.motivo_cliente}` : "Descartado"}</p>;
  return <p className={className}>{porQue(k)}</p>;
}

/** El «por qué» en una línea. */
export function porQue(k: { veredicto: Veredicto; motivo: string | null; criterios: unknown[] }): string {
  if (k.motivo) return k.motivo;
  if (k.veredicto === "cumple") return k.criterios.length ? `Cumple los ${k.criterios.length} requisitos de la vacante` : "Cumple los requisitos de la vacante";
  return ETIQUETA_VEREDICTO[k.veredicto];
}

function Marcas({ k, s = 22, textos }: { k: CandidatoPortal; s?: number; textos: TextosRequisitos }) {
  return <Requisitos crit={k.criterios} textos={k.vacante_id ? textos.get(k.vacante_id) : undefined} s={s} />;
}

type Comunes = {
  abrir: (id: string) => void;
  menu: (k: CandidatoPortal) => void;
  descartar: (k: CandidatoPortal) => void;
  regresar: (k: CandidatoPortal) => void;
  puedeMover: boolean;
  esNuevo: (k: CandidatoPortal) => boolean;
  /** Candado de pago del cliente: sin atajos de contacto (igual que la ficha). */
  bloqueado: boolean;
  puesto: (id: string | null) => string | null;
  empresa: string;
  plantilla: string | null;
  /** Texto de cada requisito por vacante (para explicar los puntitos). */
  textos: TextosRequisitos;
};

const detener = (e: React.SyntheticEvent) => e.stopPropagation();

/** Columna a la que se manda a `k` para dejarlo en la etapa `e` (lo que entiende `moverCandidato`). */
function columnaParaEtapa(k: CandidatoPortal, e: Etapa): Columna {
  return e === "nuevo" ? (k.veredicto === "revisar" ? "revisar" : "cumple") : e;
}

/**
 * Etapas que se ofrecen en la Lista. Regla de columnas (`columnaDe`): una etapa que dejaría al candidato
 * en la misma columna sin cambiar nada se muestra deshabilitada, con el porqué. «Descartar…» siempre se
 * puede (pide motivo) y, si ya lo descartó el cliente, aparece «Regresar a activos».
 */
const REGRESAR = "__regresar";
const DESCARTAR = "__descartar";
function opcionesEtapa(k: CandidatoPortal) {
  const ops: { v: string; col: Columna | null; texto: string; deshabilitada: boolean }[] = ETAPAS.filter(
    (e) => e !== "descartado",
  ).map((e) => {
    const col = columnaParaEtapa(k, e);
    const destino = columnaDe(k.veredicto, etapaDeColumna(col));
    const sinEfecto = e !== k.etapa && destino === k.columna;
    // «Sin contactar» se lee como su columna (Cumple o Revisar), para que el selector siga el orden del Tablero.
    let texto = e === "nuevo" && k.veredicto !== "descartado" ? `${ETIQUETA_COLUMNA[col]} (sin contactar)` : ETAPA_CLIENTE[e];
    if (sinEfecto) texto += ` (ya está en ${ETIQUETA_COLUMNA[k.columna]} por el filtro)`;
    else if (e !== k.etapa && destino !== col) texto += ` (sigue en ${ETIQUETA_COLUMNA[destino]} por el filtro)`;
    return { v: e, col, texto, deshabilitada: sinEfecto };
  });
  if (k.etapa === "descartado") {
    ops.push({ v: "descartado", col: null, texto: k.motivo_cliente ? `Descartado: ${k.motivo_cliente}` : "Descartado", deshabilitada: false });
    ops.push({ v: REGRESAR, col: null, texto: "Regresar a activos", deshabilitada: false });
    ops.push({ v: DESCARTAR, col: null, texto: "Cambiar motivo…", deshabilitada: false });
  } else ops.push({ v: DESCARTAR, col: null, texto: "Descartar…", deshabilitada: false });
  return ops;
}

/** WhatsApp y CV en un toque, sin abrir la ficha. No aparecen con el candado de pago. */
function Contacto({ k, puesto, empresa, plantilla, bloqueado }: Pick<Comunes, "puesto" | "empresa" | "plantilla" | "bloqueado"> & { k: CandidatoPortal }) {
  const { api } = usePortal();
  if (bloqueado || k.bloqueado) return null;
  const quien = k.nombre ?? "el candidato";
  const wa = linkWhatsApp(k.telefono, k.nombre, puesto(k.vacante_id), empresa, plantilla);
  const abrirCv = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!k.cv_url) return;
    // La pestaña se abre en el mismo clic (si no, el navegador la bloquea) y luego recibe la liga firmada.
    const w = window.open("", "_blank");
    if (w) w.opener = null;
    api
      .urlCv(k.cv_url)
      .then((u) => {
        if (!u) throw new Error("El CV está en la hoja de la agencia; pídeselo si lo necesitas.");
        if (w) w.location.href = u;
        else window.open(u, "_blank", "noopener");
        if (u.startsWith("blob:")) setTimeout(() => URL.revokeObjectURL(u), 60_000);
      })
      .catch((err) => {
        w?.close();
        toast.error(mensajeError(err, "No se pudo abrir el CV."));
      });
  };
  return (
    <span className="ctc" onClick={detener} onKeyDown={detener}>
      {wa ? (
        <a className="ico wa" href={wa} target="_blank" rel="noopener noreferrer" title={`Escribirle por WhatsApp a ${quien}`} aria-label={`WhatsApp a ${quien}`}>
          <IcWhatsApp s={20} relleno /> WhatsApp
        </a>
      ) : (
        <button type="button" className="ico" aria-disabled="true" title="No tenemos su teléfono" aria-label={`Sin teléfono de ${quien}`}>
          <IcWhatsApp s={18} /> Sin teléfono
        </button>
      )}
      <button
        type="button"
        className="ico cv"
        aria-disabled={k.cv_url ? undefined : "true"}
        title={k.cv_url ? "Ver CV" : "Sin CV"}
        aria-label={k.cv_url ? `Ver CV de ${quien}` : `Sin CV de ${quien}`}
        onClick={abrirCv}
      >
        <FileText size={18} aria-hidden="true" /> {k.cv_url ? "Ver CV" : "Sin CV"}
      </button>
    </span>
  );
}

export function Candidatos() {
  const { api, sesion, soloLectura, bloqueado } = usePortal();
  const r = useRuta();
  // La pestaña se recuerda por usuario (en este navegador).
  const clavePestana = clavePestanaDe(sesion);
  const pPestana = r.params.get("grupo");
  // Arranca en «Todos» (todas las etapas a la vista); quien ya tenía «Activos» guardado pasa a Todos una vez.
  const prefPestana = pestanaRecordada(clavePestana, PREF);
  const pestana: Pestana = esPestana(pPestana) ? pPestana : prefPestana;
  const vacs = useCarga(() => api.inicio(), [api], undefined, claveCache(sesion, api.verComo, "inicio"));
  const lista = vacs.datos ?? [];

  const pVista = r.params.get("vista");
  const prefVista = leerPref("vista");
  const vista: Vista = esVista(pVista) ? pVista : esVista(prefVista) ? prefVista : "tablero";
  const valida = (v: string | null) => !!v && (v === "todas" || lista.some((x) => x.id === v));
  const pVac = r.params.get("vacante");
  const prefVac = leerPref("vacante");
  const vacante: string | null = valida(pVac) ? pVac : valida(prefVac) ? prefVac : (lista[0]?.id ?? null);
  // Búsqueda por nombre o teléfono: se escribe aquí y se refleja en la URL (?q=), así al volver de la ficha sigue.
  const pQ = r.params.get("q") ?? "";
  const [q, setQ] = useState(pQ);
  // Si la URL cambia por fuera (una liga, «atrás» del navegador), manda la URL.
  const [pQAntes, setPQAntes] = useState(pQ);
  if (pQ !== pQAntes) {
    setPQAntes(pQ);
    if (pQ !== q.trim()) setQ(pQ);
  }
  const qLimpia = q.trim();
  // Plaza (Querétaro, Monterrey…): se recuerda por usuario, como la pestaña. «todas» = sin filtro.
  const clavePlaza = clavePlazaDe(sesion);
  const pPlaza = r.params.get("plaza");
  const prefPlaza = leerPref(clavePlaza);
  const plaza: ClavePlaza | null = esClavePlaza(pPlaza) ? pPlaza : pPlaza === "todas" ? null : esClavePlaza(prefPlaza) ? prefPlaza : null;
  // Lo que llega de un número de Inicio: una etapa («Sin contactar»…) o solo los nuevos. No se recuerda.
  const pEtapa = r.params.get("etapa");
  const etapa: EtapaFiltro | null = esEtapaFiltro(pEtapa) ? pEtapa : null;
  const soloNuevos = r.params.get("nuevos") === "1";
  /** La ruta de esta pantalla con lo que se ve ahora, cambiando solo lo que se pase. */
  const aqui = (c: Partial<Record<"vacante" | "vista" | "grupo" | "q" | "plaza" | "etapa" | "nuevos", string | null>> = {}) =>
    ruta("candidatos", {
      vacante,
      vista,
      grupo: pestana,
      plaza: plaza ?? "todas",
      etapa,
      nuevos: soloNuevos ? "1" : null,
      q: qLimpia,
      ...c,
    });

  // La URL siempre refleja lo que se ve, y se recuerda para la próxima.
  useEffect(() => {
    // Solo mientras esta pantalla es la visible (evita pisar la ruta al abrir una ficha).
    if (!vacs.datos || leerRuta(window.location.hash).pantalla !== "candidatos") return;
    guardarPref("vista", vista);
    // La pestaña NO se guarda aquí: solo cuando la persona la elige (cambiarPestana). Así una liga de Inicio
    // («Contactados», que abre Activos) no le cambia la pestaña de arranque.
    guardarPref(clavePlaza, plaza ?? "todas");
    if (vacante) guardarPref("vacante", vacante);
    if (pVista !== vista || pPestana !== pestana || (vacante && pVac !== vacante) || pQ !== qLimpia || pPlaza !== (plaza ?? "todas"))
      navegar(aqui(), true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `aqui` se arma con estos mismos valores
  }, [vacs.datos, vista, vacante, pVista, pVac, pestana, pPestana, clavePestana, pQ, qLimpia, plaza, pPlaza, clavePlaza, etapa, soloNuevos]);

  const cands = useCarga(
    () => (vacante ? api.candidatos(vacante === "todas" ? null : vacante) : Promise.resolve([] as CandidatoPortal[])),
    [api, vacante],
    undefined,
    vacante ? claveCache(sesion, api.verComo, "candidatos:" + vacante) : null,
  );
  const activas = useMemo(() => new Set(lista.map((v) => v.id)), [lista]);
  const items = useMemo(
    () => (cands.datos ?? []).filter((k) => vacante !== "todas" || (k.vacante_id && activas.has(k.vacante_id))),
    [cands.datos, vacante, activas],
  );
  const puesto = (id: string | null) => lista.find((v) => v.id === id)?.puesto ?? null;
  // Los textos de los requisitos (para decir cuáles cumple al tocar los puntitos). Si no llegan, se usa la clave.
  const detalle = useCarga(() => api.vacantes(), [api], undefined, claveCache(sesion, api.verComo, "vacantes"));
  const textos = useMemo(() => textosDeVacantes(detalle.datos), [detalle.datos]);
  // La plaza filtra todo (pestañas incluidas); los chips cuentan dentro de la pestaña que se ve.
  const enPlaza = useMemo(() => (plaza ? items.filter((k) => plazaDe(k.ciudad) === plaza) : items), [items, plaza]);
  const conteo = useMemo(() => {
    const n: Record<Pestana, number> = { activos: 0, algun_dia: 0, contratados: 0, descartados: 0, todos: enPlaza.length };
    enPlaza.forEach((k) => n[grupoDe(k.columna)]++);
    return n;
  }, [enPlaza]);
  const enPestanaTodas = useMemo(() => items.filter((k) => enPestana(k, pestana)), [items, pestana]);
  const conteoPlaza = useMemo(() => contarPorPlaza(enPestanaTodas), [enPestanaTodas]);
  // Los chips de plaza salen si hay candidatos de más de una plaza en la vacante (o si ya hay una elegida).
  const hayPlazas = useMemo(() => !!plaza || new Set(items.map((k) => plazaDe(k.ciudad))).size > 1, [items, plaza]);
  const { ultima_visita, solo_lectura } = sesion;
  const esNuevo = useMemo(
    // nuevo = entró a la base después de tu última visita (la hoja solo trae la fecha; ingresado_en trae la hora)
    () => (k: CandidatoPortal) =>
      !!ultima_visita && !solo_lectura && Date.parse(k.ingresado_en ?? k.recibido_en) > Date.parse(ultima_visita),
    [ultima_visita, solo_lectura],
  );
  const enLaPestana = useMemo(() => enPlaza.filter((k) => enPestana(k, pestana)), [enPlaza, pestana]);
  // Etapa o «solo nuevos» (vienen de Inicio).
  const pasaInicio = useMemo(
    () => (k: CandidatoPortal) => (!etapa || k.etapa === etapa) && (!soloNuevos || esNuevoDesde(k, ultima_visita)),
    [etapa, soloNuevos, ultima_visita],
  );
  const filtro = useMemo(() => (k: CandidatoPortal) => (!plaza || plazaDe(k.ciudad) === plaza) && pasaInicio(k), [plaza, pasaInicio]);
  const filtrados = useMemo(() => enLaPestana.filter(pasaInicio), [enLaPestana, pasaInicio]);
  // Lo que se ve: pestaña, plaza, etapa y, si hay búsqueda, solo los que coinciden (los conteos de las pestañas no cambian).
  const visibles = useMemo(() => (qLimpia ? filtrados.filter((k) => coincide(k, qLimpia)) : filtrados), [filtrados, qLimpia]);
  const enTodos = useMemo(() => (qLimpia ? enPlaza.filter((k) => coincide(k, qLimpia)).length : 0), [enPlaza, qLimpia]);
  const enTodoSinFiltros = useMemo(() => (qLimpia ? items.filter((k) => coincide(k, qLimpia)).length : 0), [items, qLimpia]);
  const nuevos = useMemo(
    () => enLaPestana.filter((k) => !k.bloqueado && grupoDe(k.columna) === "activos" && esNuevo(k)),
    [enLaPestana, esNuevo],
  );

  // Orden y filtros de la Lista: viven aquí (y en sessionStorage) para que al volver de la ficha sigan igual
  // y para que Anterior/Siguiente recorra exactamente lo que se ve.
  const [estLista, setEstLista] = useState<EstadoLista>(leerEstadoLista);
  const cambiarLista = (e: EstadoLista) => {
    setEstLista(e);
    guardarEstadoLista(e);
  };

  const [menuDe, setMenuDe] = useState<CandidatoPortal | null>(null);
  const [descartarA, setDescartarA] = useState<CandidatoPortal | null>(null);
  const aplicar = (id: string, f: (x: CandidatoPortal) => CandidatoPortal) =>
    cands.setDatos((l) => (l ? l.map((x) => (x.id === id ? f(x) : x)) : l));
  const mover = (id: string, col: Columna) => {
    const k = cands.datos?.find((x) => x.id === id);
    if (k) moverCandidato(api, k, col, aplicar, soloLectura, setDescartarA);
  };
  const regresar = (k: CandidatoPortal) => regresarCandidato(api, k, aplicar, soloLectura);
  const actual = (k: CandidatoPortal) => items.find((x) => x.id === k.id) ?? k;

  const comunes: Comunes = {
    abrir: (id) => {
      // La secuencia de la vista, en su orden, para Anterior/Siguiente dentro de la ficha.
      const ids = ordenDeVista(items, vista, pestana, estLista, qLimpia, filtro).map((k) => k.id);
      guardarSecuencia({
        ids: ids.includes(id) ? ids : [id, ...ids],
        volver: aqui(),
        etiqueta:
          etiquetaVista(pestana, vista) +
          (plaza ? ` · ${plazaPorClave(plaza).corta}` : "") +
          (etapa ? ` · ${ETIQUETA_ETAPA_FILTRO[etapa]}` : "") +
          (soloNuevos ? " · nuevos" : "") +
          (qLimpia ? ` · «${qLimpia}»` : ""),
      });
      navegar(`#/candidato/${id}`);
    },
    menu: setMenuDe,
    descartar: setDescartarA,
    regresar,
    puedeMover: !soloLectura && !bloqueado,
    esNuevo,
    bloqueado,
    puesto,
    empresa: sesion.cliente.nombre,
    plantilla: sesion.cliente.mensaje_whatsapp ?? null,
    textos,
  };

  const cambiarVista = (v: Vista) => {
    guardarPref("vista", v);
    navegar(aqui({ vista: v }), true);
  };
  const cambiarVacante = (v: string) => {
    guardarPref("vacante", v);
    navegar(aqui({ vacante: v }), true);
  };
  // Cambiar de pestaña quita el filtro de etapa o de nuevos que vino de Inicio (si no, casi siempre saldría vacía).
  const cambiarPestana = (p: Pestana) => {
    guardarPref(clavePestana, p);
    navegar(aqui({ grupo: p, etapa: null, nuevos: null }), true);
  };
  const cambiarPlaza = (p: ClavePlaza | null) => {
    guardarPref(clavePlaza, p ?? "todas");
    navegar(aqui({ plaza: p ?? "todas" }), true);
  };
  const quitarFiltroInicio = () => navegar(aqui({ etapa: null, nuevos: null }), true);

  if (vacs.cargando && !vacs.datos)
    return (
      <>
        <div className="phello">
          <h1>Candidatos</h1>
        </div>
        <Esqueleto filas={3} alto={110} />
      </>
    );
  if (vacs.error) return <CajaError texto={vacs.error} onReintentar={vacs.recargar} />;
  if (!lista.length)
    return (
      <>
        <div className="phello">
          <h1>Candidatos</h1>
        </div>
        <p className="empty">Todavía no hay candidatos. Llegan en cuanto firmes los criterios de tu vacante y arranque el anuncio.</p>
        <a className="btn" href="#/vacantes" style={{ alignSelf: "flex-start" }}>
          Ver mis vacantes
        </a>
      </>
    );

  const donde = vacante === "todas" ? "todas tus vacantes" : (puesto(vacante) ?? "").toLowerCase();
  const nombrePestana = PESTANAS.find(([p]) => p === pestana)![1].toLowerCase();
  const dePlaza = plaza ? ` · ${plazaPorClave(plaza).corta}` : "";
  const filtroInicio = etapa ? ETIQUETA_ETAPA_FILTRO[etapa] : soloNuevos ? "Nuevos desde tu última visita" : null;
  // «3 sin contactar», «1 contactado», «2 nuevos desde tu última visita»
  const n = filtrados.length;
  const cuantosInicio = etapa
    ? `${n} ${etapa === "nuevo" ? "sin contactar" : etapa === "contactado" ? (n === 1 ? "contactado" : "contactados") : n === 1 ? "entrevistado" : "entrevistados"}`
    : `${n} ${n === 1 ? "nuevo" : "nuevos"} desde tu última visita`;
  return (
    <>
      <div className="phello">
        <h1>Candidatos</h1>
        <p>
          {cands.cargando && !cands.datos
            ? "Cargando…"
            : filtroInicio
              ? `${cuantosInicio} de ${items.length} en ${donde}${dePlaza}`
              : pestana === "todos"
                ? `${enPlaza.length} en ${donde}${dePlaza}`
                : `${enLaPestana.length} ${nombrePestana} de ${items.length} en ${donde}${dePlaza}`}
        </p>
      </div>
      {bloqueado && <BannerPago />}
      <Buscador valor={q} onValor={setQ} />
      <div className="ctrls">
        {lista.length > 0 && (
          <div className="vsel">
            <label htmlFor="vsel">Vacante</label>
            <select id="vsel" value={vacante ?? ""} onChange={(e) => cambiarVacante(e.target.value)}>
              {lista.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.puesto}
                </option>
              ))}
              {lista.length > 1 && <option value="todas">Todas tus vacantes</option>}
            </select>
          </div>
        )}
        <div className="modes" role="group" aria-label="Cómo ver a tus candidatos">
          {VISTAS.map(([v, l, I]) => (
            <button key={v} aria-pressed={vista === v} onClick={() => cambiarVista(v)}>
              <I size={16} aria-hidden="true" />
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="gbar">
        <div className="grupos" role="group" aria-label="Qué candidatos ver">
          {PESTANAS.map(([p, l]) => {
            const { clase, Icono } = PESTANA_VISUAL[p];
            return (
              <button key={p} className={clase} aria-pressed={pestana === p} onClick={() => cambiarPestana(p)}>
                <span className="eti" aria-hidden="true">
                  <Icono size={15} strokeWidth={2.6} />
                </span>
                {l} <span className="num">({cands.datos ? conteo[p] : "…"})</span>
              </button>
            );
          })}
        </div>
      </div>
      {cands.datos && hayPlazas && (
        <div className="plazas" role="group" aria-label="Filtrar por plaza">
          <span className="plazas-t">Plaza</span>
          <button type="button" className={clasePlaza(null)} aria-pressed={!plaza} onClick={() => cambiarPlaza(null)}>
            <IconoPlaza size={17} strokeWidth={2.4} aria-hidden="true" className="pin" />
            Todas <span className="num">({enPestanaTodas.length})</span>
          </button>
          {TODAS_LAS_PLAZAS.filter((p) => conteoPlaza[p.clave] > 0 || plaza === p.clave).map((p) => (
            <button
              key={p.clave}
              type="button"
              className={clasePlaza(p.clave)}
              aria-pressed={plaza === p.clave}
              title={p.etiqueta}
              onClick={() => cambiarPlaza(plaza === p.clave ? null : p.clave)}
            >
              <IconoPlaza size={17} strokeWidth={2.4} aria-hidden="true" className="pin" />
              {p.corta} <span className="num">({conteoPlaza[p.clave]})</span>
            </button>
          ))}
        </div>
      )}
      {filtroInicio && (
        <p className="factivo" role="status">
          <span>
            Solo: <b>{filtroInicio}</b>
          </span>
          <button type="button" className="factivo-x" onClick={quitarFiltroInicio} aria-label={`Quitar el filtro ${filtroInicio}`}>
            <X size={14} aria-hidden="true" /> Quitar
          </button>
        </p>
      )}
      {qLimpia && cands.datos && visibles.length > 0 && (
        <p className="bres" role="status">
          {visibles.length === 1 ? "1 coincide" : `${visibles.length} coinciden`} con «{qLimpia}»
        </p>
      )}

      {nuevos.length > 0 && !filtroInicio && (
        <section className="novedades" aria-label="Nuevos desde tu última visita">
          <b>
            <Nuevo /> {nuevos.length === 1 ? "1 nuevo" : `${nuevos.length} nuevos`} desde tu última visita
          </b>
          <ul>
            {nuevos.slice(0, 8).map((k) => (
              <li key={k.id}>
                <button onClick={() => comunes.abrir(k.id)} aria-label={`Abrir ficha de ${k.nombre} (nuevo)`}>
                  <Mk k={semDeVeredicto(k.veredicto)} s={14} titulo={TITULO_VEREDICTO[k.veredicto]} />
                  {k.nombre}
                </button>
              </li>
            ))}
            {nuevos.length > 8 && (
              <li className="muted" style={{ alignSelf: "center", fontSize: 13 }}>
                y {nuevos.length - 8} más
              </li>
            )}
          </ul>
        </section>
      )}

      {cands.datos && visibles.length > 0 && <Leyenda />}

      {cands.cargando && !cands.datos ? (
        <Esqueleto filas={3} alto={110} />
      ) : cands.error ? (
        <CajaError texto={cands.error} onReintentar={cands.recargar} />
      ) : qLimpia && !visibles.length ? (
        <div className="empty bvacio" role="status">
          {pestana !== "todos" && enTodos > 0 ? (
            <>
              <p>
                Nadie en {nombrePestana} coincide con «{qLimpia}».
              </p>
              <button type="button" className="btn primary" onClick={() => cambiarPestana("todos")}>
                Hay {enTodos} en Todos
              </button>
            </>
          ) : (plaza || filtroInicio) && enTodoSinFiltros > 0 ? (
            <>
              <p>
                Con {[plaza && plazaPorClave(plaza).corta, filtroInicio].filter(Boolean).join(" y ")} nadie coincide con «{qLimpia}».
              </p>
              <button type="button" className="btn primary" onClick={() => navegar(aqui({ plaza: "todas", etapa: null, nuevos: null }), true)}>
                Hay {enTodoSinFiltros} sin filtros
              </button>
            </>
          ) : (
            <>
              <p>Nadie coincide con «{qLimpia}».</p>
              <button type="button" className="btn ghost" onClick={() => setQ("")}>
                Limpiar búsqueda
              </button>
            </>
          )}
        </div>
      ) : !visibles.length && items.length && (plaza || filtroInicio) ? (
        <div className="empty bvacio" role="status">
          <p>
            {filtroInicio ? `No hay ${filtroInicio.toLowerCase()}` : `No hay ${nombrePestana}`}
            {plaza ? ` de ${plazaPorClave(plaza).etiqueta}` : ""} en {donde}.
          </p>
          <div className="row" style={{ gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
            {plaza && (
              <button type="button" className="btn primary" onClick={() => cambiarPlaza(null)}>
                Ver todas las plazas
              </button>
            )}
            {filtroInicio && (
              <button type="button" className={plaza ? "btn ghost" : "btn primary"} onClick={quitarFiltroInicio}>
                Quitar «{filtroInicio}»
              </button>
            )}
          </div>
        </div>
      ) : !visibles.length && items.length ? (
        <p className="empty">
          {pestana === "activos"
            ? "No tienes candidatos activos aquí. Revisa Algún día, Contratados o Descartados."
            : pestana === "algun_dia"
              ? "No has guardado a nadie para algún día. Con «Cambiar etapa» puedes guardarlos aquí."
              : pestana === "contratados"
              ? "Todavía no marcas a nadie como contratado."
              : "No hay descartados."}
        </p>
      ) : vista === "tablero" ? (
        <Tablero items={visibles} pestana={pestana} soloEtapa={etapa} mover={mover} {...comunes} />
      ) : vista === "lista" ? (
        <Lista items={visibles} estado={estLista} onEstado={cambiarLista} mover={mover} {...comunes} />
      ) : (
        <Tarjetas items={visibles} {...comunes} />
      )}

      {menuDe && (
        <HojaMover
          nombre={menuDe.nombre ?? "este candidato"}
          actual={actual(menuDe).columna}
          etapa={actual(menuDe).etapa}
          onElegir={(c) => mover(menuDe.id, c)}
          onDescartar={() => setDescartarA(actual(menuDe))}
          onRegresar={() => regresar(actual(menuDe))}
          onCerrar={() => setMenuDe(null)}
        />
      )}
      {descartarA && (
        <HojaDescartar
          nombre={descartarA.nombre ?? "este candidato"}
          inicial={descartarA.etapa === "descartado" ? descartarA.motivo_cliente : null}
          onConfirmar={(m) => descartarCandidato(api, actual(descartarA), m, aplicar, soloLectura)}
          onCerrar={() => setDescartarA(null)}
        />
      )}
    </>
  );
}

/* ---------------- Buscador ---------------- */

function Buscador({ valor, onValor }: { valor: string; onValor: (v: string) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  // Grande, a todo lo ancho y con su etiqueta a la vista: la RH no veía el buscador chico de la esquina (5-oct).
  return (
    <div className="buscar" role="search">
      <label htmlFor="buscar-candidato" className="buscar-et">
        Buscar candidato
      </label>
      <div className="buscar-caja">
      <Search size={22} aria-hidden="true" className="buscar-lupa" />
      <input
        ref={ref}
        id="buscar-candidato"
        type="search"
        value={valor}
        onChange={(e) => onValor(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== "Escape") return;
          e.preventDefault();
          if (valor) onValor("");
          else ref.current?.blur();
        }}
        placeholder="Escribe nombre o teléfono"
        aria-describedby="buscar-ayuda"
        autoComplete="off"
        spellCheck={false}
        enterKeyHint="search"
      />
      {valor && (
        <button
          type="button"
          className="buscar-x"
          aria-label="Limpiar búsqueda"
          title="Limpiar (Esc)"
          onClick={() => {
            onValor("");
            ref.current?.focus();
          }}
        >
          <X size={18} aria-hidden="true" /> Limpiar
        </button>
      )}
      </div>
      <span id="buscar-ayuda" className="sr">
        Busca por nombre o por teléfono entre los candidatos que estás viendo.
      </span>
    </div>
  );
}

/* ---------------- Tablero ---------------- */

function Tablero({
  items,
  pestana,
  soloEtapa,
  mover,
  ...c
}: Comunes & {
  items: CandidatoPortal[];
  pestana: Pestana;
  /** Con un filtro de etapa (de Inicio), solo sus columnas: «Sin contactar» = Cumple y Revisar. */
  soloEtapa: EtapaFiltro | null;
  mover: (id: string, col: Columna) => void;
}) {
  const angosto = useAngosto(640);
  const arrastrable = c.puedeMover && !angosto;
  const columnas = useMemo(
    () => COLUMNAS_DE[pestana].filter((col) => !soloEtapa || etapaDeColumna(col) === soloEtapa),
    [pestana, soloEtapa],
  );
  // En cada columna, los más recientes primero (siempre; no solo los nuevos desde tu última visita).
  const porCol = useMemo(() => {
    const m = {} as Record<Columna, CandidatoPortal[]>;
    columnas.forEach((col) => (m[col] = []));
    ordenTablero(items, pestana).forEach((k) => m[k.columna]?.push(k));
    return m;
  }, [items, pestana, columnas]);

  const soltar = (res: DropResult) => {
    const destino = res.destination?.droppableId as Columna | undefined;
    if (!destino || destino === res.source.droppableId) return;
    mover(res.draggableId, destino);
  };

  const col = (id: Columna) => {
    const l = porCol[id];
    return (
      <Droppable key={id} droppableId={id} isDropDisabled={!arrastrable}>
        {(prov, snap) => (
          <section className={`col ${claseEtapa(id)}` + (snap.isDraggingOver ? " over" : "")} aria-label={`${ETIQUETA_COLUMNA[id]}: ${l.length}`}>
            <h2 className="col-h">
              <IconoEtapa c={id} s={34} className="col-ic" />
              {ETIQUETA_COLUMNA[id]}
              <span className="n">{l.length}</span>
            </h2>
            <p className="col-sub">{SUBTITULO_COLUMNA[id]}</p>
            <div className="col-body" ref={prov.innerRef} {...prov.droppableProps}>
              {l.map((k, i) => (
                <Draggable key={k.id} draggableId={k.id} index={i} isDragDisabled={!arrastrable || k.bloqueado}>
                  {(p, s) => (
                    <div ref={p.innerRef} {...p.draggableProps} {...p.dragHandleProps} style={p.draggableProps.style as React.CSSProperties}>
                      <TarjetaTablero k={k} arrastrando={s.isDragging} {...c} />
                    </div>
                  )}
                </Draggable>
              ))}
              {prov.placeholder}
              {!l.length && <p className="col-vacio">Nadie aquí por ahora</p>}
            </div>
          </section>
        )}
      </Droppable>
    );
  };

  const nav = useNavTablero(columnas);
  const varias = columnas.length > 1;
  const titulo = (id: Columna) => ETIQUETA_COLUMNA[id];

  return (
    <>
      {varias && (
        <nav className="etapas-barra" aria-label="Ir a una columna del tablero" ref={nav.barraRef}>
          {columnas.map((id, i) => (
            <button
              key={id}
              type="button"
              className={claseEtapa(id)}
              aria-current={nav.visibles.includes(i) ? "true" : undefined}
              title={`Ir a «${titulo(id)}»`}
              onClick={() => nav.irA(i)}
            >
              <IconoEtapa c={id} s={24} />
              <span className="eb-t">{titulo(id)}</span>
              <span className="eb-n">{porCol[id].length}</span>
            </button>
          ))}
        </nav>
      )}
      <div className={"board-wrap" + (nav.izq ? " hay-izq" : "") + (nav.der ? " hay-der" : "")}>
        {varias && !angosto && (
          <>
            <div className="board-flecha izq">
              <button type="button" onClick={() => nav.paso(-1)} disabled={!nav.izq} aria-label="Ver la columna de la izquierda" title="Columna anterior">
                <ChevronLeft size={34} strokeWidth={2.6} aria-hidden="true" />
              </button>
            </div>
            <div className="board-flecha der">
              <button type="button" onClick={() => nav.paso(1)} disabled={!nav.der} aria-label="Ver la columna de la derecha" title="Siguiente columna">
                <ChevronRight size={34} strokeWidth={2.6} aria-hidden="true" />
              </button>
            </div>
          </>
        )}
        <DragDropContext onDragEnd={soltar}>
          <div className={"board" + (columnas.length === 1 ? " una" : "")} ref={nav.boardRef}>
            {columnas.map((id) => col(id))}
          </div>
        </DragDropContext>
      </div>
      {varias && angosto && (
        <div className="board-puntos">
          <span className="sr" aria-live="polite">
            Columna {nav.actual + 1} de {columnas.length}: {titulo(columnas[nav.actual] ?? columnas[0])}
          </span>
          {columnas.map((id, i) => (
            <button
              key={id}
              type="button"
              className={claseEtapa(id)}
              aria-current={i === nav.actual ? "true" : undefined}
              aria-label={`Ir a «${titulo(id)}»`}
              onClick={() => nav.irA(i)}
            />
          ))}
        </div>
      )}
      <p className="muted" style={{ fontSize: 13 }}>
        {!varias
          ? c.puedeMover
            ? "Para moverlos, toca «Cambiar etapa»."
            : ""
          : angosto
            ? "Desliza de lado o toca un nombre de arriba para ver otra columna." + (c.puedeMover ? " Usa «Cambiar etapa» para moverlos." : "")
            : "Usa las flechas ◀ ▶, toca un nombre de arriba o gira la rueda del ratón sobre los títulos para ver más columnas." +
              (c.puedeMover ? " Para moverlos, toca «Cambiar etapa» o arrastra la tarjeta a otra columna." : "")}
      </p>
    </>
  );
}

/**
 * Cómo se recorre el Tablero de lado (Andrés, 6-oct: la RH no sabía que había más columnas a la derecha):
 * qué columnas se ven, si hay más a la izquierda o a la derecha, ir a una columna, avanzar de una en una, y la
 * rueda del ratón en horizontal sobre la barra de etapas y los títulos de columna (sobre las tarjetas la rueda
 * sigue bajando la página, para no atorar a nadie).
 */
function useNavTablero(columnas: Columna[]) {
  const boardRef = useRef<HTMLDivElement>(null);
  const barraRef = useRef<HTMLElement>(null);
  const [estado, setEstado] = useState({ izq: false, der: false, actual: 0, visibles: [0] as number[] });

  const medir = useCallback(() => {
    const b = boardRef.current;
    if (!b) return;
    const sl = b.scrollLeft;
    const ancho = b.clientWidth;
    // Visible = al menos 60 % de la columna en pantalla.
    const visibles = colsDe(b)
      .map((el, i) => {
        const l = el.offsetLeft - sl;
        const dentro = Math.min(l + el.offsetWidth, ancho) - Math.max(l, 0);
        return dentro >= el.offsetWidth * 0.6 ? i : -1;
      })
      .filter((i) => i >= 0);
    const actual = visibles[0] ?? 0;
    const izq = sl > 4;
    const der = sl + ancho < b.scrollWidth - 4;
    setEstado((e) =>
      e.izq === izq && e.der === der && e.actual === actual && e.visibles.join() === visibles.join() ? e : { izq, der, actual, visibles },
    );
  }, []);

  const irA = useCallback((i: number) => {
    const b = boardRef.current;
    const el = b && colsDe(b)[i];
    if (!b || !el) return;
    b.scrollTo({ left: Math.max(0, el.offsetLeft - rellenoDe(b)), behavior: "smooth" });
  }, []);

  const paso = useCallback(
    (dir: 1 | -1) => {
      const b = boardRef.current;
      if (!b) return;
      const lista = colsDe(b);
      // La primera columna que empieza a la derecha (o la última que empieza a la izquierda) del borde actual.
      const borde = b.scrollLeft + rellenoDe(b);
      const i = dir > 0 ? lista.findIndex((el) => el.offsetLeft > borde + 4) : lista.map((el) => el.offsetLeft < borde - 4).lastIndexOf(true);
      if (i >= 0) irA(i);
    },
    [irA],
  );

  useEffect(() => {
    const b = boardRef.current;
    if (!b) return;
    medir();
    // Directo, sin requestAnimationFrame: son 7 columnas y en una pestaña de fondo el rAF no corre.
    const alMover = () => medir();
    b.addEventListener("scroll", alMover, { passive: true });
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(alMover) : null;
    ro?.observe(b);
    colsDe(b).forEach((el) => ro?.observe(el));
    // Rueda vertical → de lado, solo sobre la barra de etapas, los títulos de columna y las flechas.
    const rueda = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX) || e.ctrlKey) return;
      const t = e.target as Element | null;
      if (!t?.closest(".etapas-barra, .col-h, .col-sub, .board-flecha")) return;
      if (b.scrollWidth <= b.clientWidth) return;
      e.preventDefault();
      b.scrollBy({ left: e.deltaY * (e.deltaMode === 1 ? 32 : 1) });
    };
    const zonas = [b, barraRef.current, ...Array.from(b.parentElement?.querySelectorAll<HTMLElement>(".board-flecha") ?? [])].filter(
      (x): x is HTMLElement => !!x,
    );
    zonas.forEach((z) => z.addEventListener("wheel", rueda, { passive: false }));
    return () => {
      b.removeEventListener("scroll", alMover);
      ro?.disconnect();
      zonas.forEach((z) => z.removeEventListener("wheel", rueda));
    };
  }, [medir, columnas]);

  // En celular la barra de etapas también se recorre: que la columna que se ve quede a la vista en la barra.
  useEffect(() => {
    const barra = barraRef.current;
    const boton = barra?.children[estado.actual] as HTMLElement | undefined;
    if (!barra || !boton || barra.scrollWidth <= barra.clientWidth) return;
    const l = boton.offsetLeft;
    if (l < barra.scrollLeft || l + boton.offsetWidth > barra.scrollLeft + barra.clientWidth)
      barra.scrollTo({ left: Math.max(0, l - 16), behavior: "smooth" });
  }, [estado.actual]);

  return { ...estado, boardRef, barraRef, irA, paso };
}

const colsDe = (b: HTMLElement) => Array.from(b.querySelectorAll<HTMLElement>(":scope > section.col"));
const rellenoDe = (b: HTMLElement) => parseFloat(getComputedStyle(b).paddingLeft) || 0;

function TarjetaTablero({ k, arrastrando, abrir, menu, puedeMover, esNuevo, descartar: _d, regresar: _r, ...c }: Comunes & { k: CandidatoPortal; arrastrando: boolean }) {
  const sem = semDeVeredicto(k.veredicto);
  if (k.bloqueado)
    return (
      <article className="card locked" onClick={() => abrir(k.id)}>
        <div className="foot">
          <Mk k={sem} s={16} titulo={ETIQUETA_VEREDICTO[k.veredicto]} />
          <button className="nm lockline">
            <Lock size={14} aria-hidden="true" /> Candidato bloqueado
          </button>
        </div>
        <p className="locksub">Se liberan al confirmar tu pago</p>
        <div className="foot">
          <span className="meta">{k.ciudad ?? ""}</span>
          <span className="spacer" />
          <span className="num meta">{fechaAmigable(k.recibido_en, k.ingresado_en)}</span>
        </div>
      </article>
    );
  return (
    <article className={`card ${claseEtapa(ETAPA_DE_VEREDICTO[k.veredicto])}` + (arrastrando ? " dragging" : "")} onClick={() => abrir(k.id)}>
      {esNuevo(k) && (
        <div className="foot">
          <Nuevo />
        </div>
      )}
      <div className="foot" style={{ flexWrap: "nowrap", alignItems: "flex-start" }}>
        <Mk k={sem} s={28} titulo={TITULO_VEREDICTO[k.veredicto]} />
        <button className="nm" aria-label={`Abrir ficha de ${k.nombre}`}>
          {k.nombre}
        </button>
      </div>
      <p className="res-v">{TITULO_VEREDICTO[k.veredicto]}</p>
      <Porque k={k} />
      <Faltantes crit={k.criterios} textos={k.vacante_id ? c.textos.get(k.vacante_id) : undefined} />
      {k.nota_cliente && (
        <div className="foot">
          <ConNota k={k} />
        </div>
      )}
      <Marcas k={k} textos={c.textos} />
      <div className="foot meta">
        <span>{k.ciudad ?? ""}</span>
        <span className="spacer" />
        <span>Llegó {fechaAmigable(k.recibido_en, k.ingresado_en)}</span>
      </div>
      <Contacto k={k} {...c} />
      <BotonEtapa k={k} puedeMover={puedeMover} menu={menu} />
    </article>
  );
}

/** «Cambiar etapa»: el mismo menú en Tablero y Tarjetas. */
function BotonEtapa({ k, puedeMover, menu }: Pick<Comunes, "puedeMover" | "menu"> & { k: CandidatoPortal }) {
  return (
    <button
      type="button"
      className={`cambiar ${claseEtapa(k.columna)}`}
      disabled={!puedeMover}
      onClick={(e) => {
        e.stopPropagation();
        menu(k);
      }}
      aria-label={`Cambiar etapa de ${k.nombre}`}
    >
      <IconoEtapa c={k.columna} s={26} />
      <span>Cambiar etapa</span>
      <ArrowRight size={18} aria-hidden="true" className="cambiar-f" />
    </button>
  );
}

/* ---------------- Lista ---------------- */

function Lista({
  items,
  estado,
  onEstado,
  abrir,
  mover,
  puedeMover,
  descartar,
  regresar,
  ...c
}: Comunes & {
  items: CandidatoPortal[];
  estado: EstadoLista;
  onEstado: (e: EstadoLista) => void;
  mover: (id: string, col: Columna) => void;
}) {
  const { fVer, fEta, fFec } = estado;
  const orden = { k: estado.orden, dir: estado.dir };
  const setOrden = (f: (o: { k: Orden; dir: 1 | -1 }) => { k: Orden; dir: 1 | -1 }) => {
    const o = f(orden);
    onEstado({ ...estado, orden: o.k, dir: o.dir });
  };
  const setFVer = (v: "" | Veredicto) => onEstado({ ...estado, fVer: v });
  const setFEta = (v: "" | Etapa) => onEstado({ ...estado, fEta: v });
  const setFFec = (v: "" | "3" | "7") => onEstado({ ...estado, fFec: v });

  const filas = useMemo(() => filasLista(items, estado), [items, estado]);

  const th = (k: Orden, t: string) => (
    <th aria-sort={orden.k === k ? (orden.dir > 0 ? "ascending" : "descending") : "none"}>
      <button onClick={() => setOrden((o) => ({ k, dir: o.k === k ? (o.dir === 1 ? -1 : 1) : k === "fecha" ? -1 : 1 }))}>
        {t}
        {orden.k === k && (orden.dir > 0 ? <ArrowUp size={12} aria-hidden="true" /> : <ArrowDown size={12} aria-hidden="true" />)}
      </button>
    </th>
  );

  return (
    <>
      <div className="filters">
        <select className="inp" aria-label="Filtrar por resultado" value={fVer} onChange={(e) => setFVer(e.target.value as Veredicto | "")}>
          <option value="">Resultado: todos</option>
          <option value="cumple">Cumple todo</option>
          <option value="revisar">Revisar: le falta algo</option>
          <option value="descartado">Descartado</option>
        </select>
        <select className="inp" aria-label="Filtrar por etapa" value={fEta} onChange={(e) => setFEta(e.target.value as Etapa | "")}>
          <option value="">Etapa: todas</option>
          {ETAPAS.map((e) => (
            <option key={e} value={e}>
              {ETAPA_CLIENTE[e]}
            </option>
          ))}
        </select>
        <select className="inp" aria-label="Filtrar por fecha" value={fFec} onChange={(e) => setFFec(e.target.value as "" | "3" | "7")}>
          <option value="">Llegó: cuando sea</option>
          <option value="3">Últimos 3 días</option>
          <option value="7">Últimos 7 días</option>
        </select>
      </div>
      <div className="tablewrap">
        <table className="ltable">
          <thead>
            <tr>
              {th("nombre", "Nombre")}
              <th>
                <span className="sr">Contacto</span>
              </th>
              {th("ver", "Resultado")}
              <th>Requisitos</th>
              {th("fecha", "Llegó")}
              {th("ciudad", "Ciudad")}
              {th("etapa", "Etapa")}
            </tr>
          </thead>
          <tbody>
            {filas.map((k) => (
              <tr
                key={k.id}
                className="clk"
                tabIndex={0}
                onClick={() => abrir(k.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    abrir(k.id);
                  }
                }}
                aria-label={k.bloqueado ? "Candidato bloqueado" : `Abrir ficha de ${k.nombre}`}
              >
                <td className="nm">
                  {k.bloqueado ? (
                    <span className="lockline" style={{ fontSize: 13.5 }}>
                      <Lock size={13} aria-hidden="true" /> Bloqueado
                    </span>
                  ) : (
                    <>
                      {k.nombre}
                      {c.esNuevo(k) && (
                        <>
                          {" "}
                          <Nuevo />
                        </>
                      )}
                      {k.nota_cliente && (
                        <>
                          {" "}
                          <ConNota k={k} />
                        </>
                      )}
                    </>
                  )}
                </td>
                <td className="nw ctd" onClick={detener} onKeyDown={detener}>
                  <Contacto k={k} {...c} />
                </td>
                <td className="nw">
                  <ChipEtapa c={ETAPA_DE_VEREDICTO[k.veredicto]} texto={TITULO_VEREDICTO[k.veredicto]} />
                </td>
                <td>
                  <Marcas k={k} textos={c.textos} />
                </td>
                <td className="num nw">{fechaAmigable(k.recibido_en, k.ingresado_en)}</td>
                <td className="nw">{k.ciudad ?? "—"}</td>
                {puedeMover && !k.bloqueado ? (
                  <td className="nw" onClick={detener} onKeyDown={detener}>
                    <span className="etsel-c">
                    <IconoEtapa c={k.columna} s={26} />
                    <select
                      className={`etsel et-${k.etapa === "nuevo" ? k.veredicto : k.etapa}`}
                      aria-label={`Etapa de ${k.nombre}`}
                      value={k.etapa}
                      onChange={(ev) => {
                        const v = ev.target.value;
                        if (v === DESCARTAR) return descartar(k);
                        if (v === REGRESAR) return regresar(k);
                        const o = opcionesEtapa(k).find((x) => x.v === v);
                        if (o?.col && !o.deshabilitada) mover(k.id, o.col);
                      }}
                    >
                      {opcionesEtapa(k).map((o) => (
                        <option key={o.v} value={o.v} disabled={o.deshabilitada}>
                          {o.texto}
                        </option>
                      ))}
                    </select>
                    </span>
                  </td>
                ) : (
                  <td className="nw">
                    <ChipEtapa
                      c={k.columna}
                      texto={k.etapa === "descartado" && k.motivo_cliente ? `Descartado: ${k.motivo_cliente}` : ETAPA_CLIENTE[k.etapa]}
                    />
                  </td>
                )}
              </tr>
            ))}
            {!filas.length && (
              <tr>
                <td colSpan={7} className="muted">
                  {items.length ? "Nadie con esos filtros." : "Sin candidatos todavía."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="muted" style={{ fontSize: 13 }}>
        {filas.length} de {items.length} candidatos. Toca un título de columna para ordenar.
      </p>
    </>
  );
}

/* ---------------- Tarjetas ---------------- */

function Tarjetas({ items, puesto, empresa, plantilla, abrir, menu, regresar, puedeMover, esNuevo, textos }: Comunes & { items: CandidatoPortal[] }) {
  // Los más recientes primero, siempre.
  const l = useMemo(() => ordenTarjetas(items), [items]);
  if (!l.length) return <p className="empty">Sin candidatos todavía.</p>;
  return (
    <div className="tgrid">
      {l.map((k) => {
        const sem = semDeVeredicto(k.veredicto);
        if (k.bloqueado)
          return (
            <article key={k.id} className="tcard">
              <div className="th">
                <Mk k={sem} s={26} titulo={ETIQUETA_VEREDICTO[k.veredicto]} />
                <h3 className="lockline">
                  <Lock size={16} aria-hidden="true" /> Candidato bloqueado
                </h3>
              </div>
              <p className="muted" style={{ fontSize: 14 }}>
                Se liberan al confirmar tu pago.
              </p>
              <div className="foot">
                <span className="pill neu">{ETAPA_CLIENTE[k.etapa]}</span>
                <span className="spacer" />
                <span className="num meta">
                  {k.ciudad ?? ""} · {fechaAmigable(k.recibido_en, k.ingresado_en)}
                </span>
              </div>
              <a className="btn" href="#/cuenta">
                <Lock size={16} aria-hidden="true" /> Ver cómo pagar
              </a>
            </article>
          );
        const wa = linkWhatsApp(k.telefono, k.nombre, puesto(k.vacante_id), empresa, plantilla);
        const tel = linkLlamar(k.telefono);
        return (
          <article key={k.id} className={`tcard ${claseEtapa(ETAPA_DE_VEREDICTO[k.veredicto])}`}>
            {esNuevo(k) && (
              <div className="foot">
                <Nuevo />
              </div>
            )}
            <div className="th">
              <Mk k={sem} s={30} titulo={TITULO_VEREDICTO[k.veredicto]} />
              <h3>{k.nombre}</h3>
              <ConNota k={k} />
            </div>
            <p className="res-v">{TITULO_VEREDICTO[k.veredicto]}</p>
            <Porque k={k} className="muted tw" />
            <Faltantes crit={k.criterios} textos={k.vacante_id ? textos.get(k.vacante_id) : undefined} />
            <Marcas k={k} textos={textos} />
            <div className="foot">
              <ChipEtapa c={k.columna} texto={ETAPA_CLIENTE[k.etapa]} />
              <span className="spacer" />
              <span className="meta">
                {k.ciudad ? `${k.ciudad} · ` : ""}llegó {fechaAmigable(k.recibido_en, k.ingresado_en)}
              </span>
            </div>
            <div className="acts2">
              {wa ? (
                <a className="btn wa big" href={wa} target="_blank" rel="noopener noreferrer">
                  <IcWhatsApp s={22} relleno /> WhatsApp
                </a>
              ) : (
                <button className="btn big" disabled>
                  Sin teléfono
                </button>
              )}
              {tel ? (
                <a className="btn big" href={tel}>
                  <Phone size={18} aria-hidden="true" /> Llamar
                </a>
              ) : (
                <button className="btn big" disabled>
                  Sin teléfono
                </button>
              )}
            </div>
            <div className="row">
              <button className="linkbtn" onClick={() => abrir(k.id)}>
                <FileText size={17} aria-hidden="true" /> Ver ficha y CV
              </button>
              <span className="spacer" />
              {k.etapa === "descartado" && (
                <button className="mini" disabled={!puedeMover} onClick={() => regresar(k)} aria-label={`Regresar a ${k.nombre} a activos`}>
                  <Undo2 size={15} aria-hidden="true" /> Regresar a activos
                </button>
              )}
              <BotonEtapa k={k} puedeMover={puedeMover} menu={menu} />
            </div>
          </article>
        );
      })}
    </div>
  );
}

